import { useState } from 'react';

import { DocumentCard, DocumentCardProps } from '@opendoor/cinderblocks/sell/DocumentCard';

import { Section } from 'shared/components/Section';
import {
  ClosingTasks,
  AnyClosingTask,
  HelloSignClosingTask,
} from 'sellDirect/helpers/closingTasks';
import {
  ClosedOffer,
  FinalizedOffer,
  UnderContractOffer,
  WithdrawnOffer,
} from 'sellDirect/OfferContext';
import { capitalizedSnakeCaseToWords } from 'shared/helpers/formatters';
import { useHelloSignDownload } from 'sellDirect/helpers/closingDash/helloSign';
import { FinalOfferModal } from 'sellDirect/components/closingDash/finalOffer/FinalOfferModal';
import { notEmpty } from 'sellDirect/helpers/closingDash/globals';
import { Flex, ListItem } from '@opendoor/bricks/core';

interface IDocumentsSection {
  offer: FinalizedOffer | UnderContractOffer | WithdrawnOffer | ClosedOffer;
  closingTasks: ClosingTasks;
}

// component-test-check-ignore
/** DocumentsSection for Sell Direct. */
export const DocumentsSection: React.FC<IDocumentsSection> = ({ offer, closingTasks }) => {
  const [finalOfferModalOpen, setFinalOfferModalOpen] = useState(false);
  const helloSignDownload = useHelloSignDownload();
  const helloSignDocuments: DocumentCardProps[] = offer.shouldSeeAgentExperience
    ? []
    : getCompletedHelloSignTasks(closingTasks).map((task) => ({
        type: 'button',
        id: task.taskable.id,
        name: capitalizedSnakeCaseToWords(task.taskable.documentType || ''),
        onClick: () => helloSignDownload(task.taskable.id),
      }));
  const documentItems: DocumentCardProps[] = [
    {
      type: 'button',
      id: 'offer-breakdown',
      name: 'Offer breakdown',
      onClick: () => setFinalOfferModalOpen(true),
    },
    ...helloSignDocuments,
  ];
  return (
    <>
      <Section data-testid="documents-section" title="Your info" mt="10">
        <Flex as="ul" flexDir="column" gridGap={3}>
          {documentItems.map((doc, idx) => (
            <ListItem key={doc.id}>
              <DocumentCard {...doc} testIndex={idx} />
            </ListItem>
          ))}
        </Flex>
      </Section>
      <FinalOfferModal
        offer={offer}
        closingTasks={closingTasks}
        isOpen={finalOfferModalOpen}
        onRequestClose={() => setFinalOfferModalOpen(false)}
      />
    </>
  );
};

/**
 *  @returns An array of Completed HelloSign-specific Closing Tasks
 *  @param {ClosingTasks} closingTasks - The closingTasks for the offer
 */
const getCompletedHelloSignTasks = (closingTasks: ClosingTasks): HelloSignClosingTask[] => {
  // Most properties in `closingTasks` are just a single Task<Taskable>. But there is one
  // property (closingTasks.signFormAddendums) that is Task<Taskable>[] (and there may be more in the future).
  // Therefore, run `.flat()`, and then get each `task`, get only the HelloSignTasks that have a completedAt value.
  return Object.values(closingTasks)
    .flat()
    .filter(notEmpty) // remove possible nulls/undefineds
    .filter((task: AnyClosingTask) => {
      // NOTE: even though we call `.flat()`, typescript doesn't know that, therefore we still need to check `Array.isArray`.
      return (
        task &&
        !Array.isArray(task) &&
        task.taskable.__typename === 'HelloSignTask' &&
        !!task.taskable.completedAt
      );
    }) as HelloSignClosingTask[];
};
